import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import {
  TRACKING_CHANGE_PORTAL_OPTIONS,
  userRoles,
} from "../../../../constants";
import useStepper from "../../../../hooks/useStepper";
import AutocompleteField from "../../../generic-components/autocomplete-field";
import LoadingButton from "../../../generic-components/button";
import Loader from "../../../generic-components/loader";
import { useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Box, Button, Stack, Typography, useMediaQuery } from "@mui/material";
import {
  getAllOrganisations,
  getUserOrganisations,
} from "../../../../graphql/queries/OrganisationDetails";
import { UpdateUserCurrentOrganisation } from "../../../../graphql/mutations/personalDetails";
import {
  compileOrganisationData,
  legacyAppRedirection,
  parameterizedDetail,
  sortArray,
} from "../../../../helpers";
import isEmpty from "lodash.isempty";
import lockIcon from "../../../../assets/images/lock-icon.png";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const SelectOrgRolePage = ({ user_roles_organisation }) => {
  const tablet = useMediaQuery("(max-width:900px)");
  const { personalDetails, setSaveError, setErrorToast, setFullScreenLoading } =
    useStepper();
  const navigate = useNavigate();

  const [organisations, setOrganisations] = useState([]);
  const [allOrganisations, setAllOrganisations] = useState([]);
  const [adminOrganisations, setAdminOrganisations] = useState([]);
  const [allAdminOrganisations, setAllAdminOrganisations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [fetchAllOrganisations] = useLazyQuery(getAllOrganisations);
  const [fetchUserOrganisations] = useLazyQuery(getUserOrganisations);
  const [setCurrentOrganisation] = useMutation(UpdateUserCurrentOrganisation);
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");

  const handelRedirectToBackendArea = () => {
    setFullScreenLoading(true);
    setIsLoading(true);
    window.location.href = `${process.env.REACT_APP_AVCWISE_URL}/admin/dashboard`;
  };

  const fetchUserOrganisationsDetails = () => {
    if (personalDetails.backend_user) {
      fetchAllOrganisations({
        onCompleted: (data) => {
          compileOrganisations(
            data?.organisations,
            setOrganisations,
            setAllOrganisations,
            true,
            userRoles.ORGANISATION_EMPLOYEE
          );
          compileOrganisations(
            data?.organisations,
            setAdminOrganisations,
            setAllAdminOrganisations,
            true,
            userRoles.ORGANISATION_ADMIN
          );
          setIsLoading(false);
        },
        onError: (error) => {
          setSaveError(error);
          setErrorToast(true);
          setIsLoading(false);
        },
      });
    } else {
      fetchUserOrganisations({
        variables: {
          user_id: personalDetails.id,
        },
        onCompleted: (data) => {
          const employeeOrganisations = data?.users_roles.filter(
            (role) =>
              role?.belongs_to_role?.name === userRoles.ORGANISATION_EMPLOYEE
          );
          const adminOrganisations = data?.users_roles?.filter(
            (role) =>
              role?.belongs_to_role?.name === userRoles.ORGANISATION_ADMIN
          );
          compileOrganisations(
            employeeOrganisations,
            setOrganisations,
            setAllOrganisations,
            false
          );
          compileOrganisations(
            adminOrganisations,
            setAdminOrganisations,
            setAllAdminOrganisations,
            false
          );
          setIsLoading(false);
        },
        onError: (error) => {
          setSaveError(error);
          setErrorToast(true);
          setIsLoading(false);
        },
      });
    }
  };

  const compileOrganisations = (
    organisations,
    setState,
    setAllState,
    backendUser,
    userRole
  ) => {
    let compiledOrganisations = compileOrganisationData(
      organisations,
      backendUser,
      userRole
    );
    setAllState(compiledOrganisations || []);
    setState(compiledOrganisations || []);
  };

  const findOrganisation = (title, search) => {
    for (let i = 0; i <= title?.length - search?.length; i++) {
      if (title.substring(i, i + search?.length) === search) {
        return true;
      }
    }
    return false;
  };

  const getOrganisations = (search, organisationData, setState) => {
    if (search?.length) {
      const filteredOrganisations = organisationData.filter((organisation) =>
        findOrganisation(
          organisation?.label?.toLowerCase(),
          search?.toLowerCase()
        )
      );
      setState(filteredOrganisations);
    } else {
      setState(organisationData);
    }
  };

  const getOrganisationById = async (
    value,
    organisationData,
    isAdminDropDown
  ) => {
    if (!value) {
      setSelectedOrganisation(null);
      setSelectedRole("");
      return;
    }
    const organisation = organisationData.find(
      (organisation) => organisation.value === value
    );
    setSelectedOrganisation(organisation);
    if (isAdminDropDown) {
      setSelectedRole("admin");
    } else {
      setSelectedRole("employee");
    }
  };

  const handleOrganisationChange = async () => {
    setIsLoading(true);
    setFullScreenLoading(true);
    setCurrentOrganisation({
      variables: {
        user_id: personalDetails?.id,
        current_organisation_id: selectedOrganisation?.value,
        current_role_id: selectedOrganisation?.role,
      },
      onCompleted: () => {
        sessionStorage.setItem("backRedirectRoute", window.location.pathname);
        const redirectionURL = selectedOrganisation?.subDomain
          ? `https://${selectedOrganisation?.subDomain}.${process.env.REACT_APP_GENERIC_URL}`
          : window.location.origin;

        if (selectedRole === "admin") {
          legacyAppRedirection(
            selectedOrganisation?.subDomain,
            `/organisations/${parameterizedDetail(
              selectedOrganisation?.value,
              selectedOrganisation?.label
            )}/dashboard`
          );
          return;
        }

        if (selectedOrganisation?.isNHS) {
          legacyAppRedirection(
            selectedOrganisation?.subDomain,
            "/schemes/my_salary_sacrifice_avc"
          );
          return;
        }

        window.location.href = redirectionURL;
      },
      onError: (error) => {
        setIsLoading(false);
        setFullScreenLoading(false);
        setSaveError(error);
        setErrorToast(true);
      },
    });
  };

  useEffect(() => {
    if (
      !isEmpty(personalDetails) &&
      personalDetails.role_id &&
      !personalDetails.is_employer_and_employee &&
      !personalDetails.backend_user &&
      user_roles_organisation.length <= 1
    ) {
      navigate("/home");
    } else if (
      !isEmpty(personalDetails) &&
      (personalDetails.is_employer_and_employee ||
        personalDetails.backend_user ||
        user_roles_organisation.length > 1)
    ) {
      fetchUserOrganisationsDetails();
    }
  }, [personalDetails]);

  const initialValues = {
    organisations: "",
    adminOrganisations: "",
  };

  const goBack = () => {
    if (document?.referrer?.includes(window?.location?.origin)) {
      navigate(-1); // Client-side navigation
    } else {
      window?.history?.back(); // External navigation
    }
  };

  return (
    <Box className="select-org-container">
      <img src={lockIcon} alt="lock icon" className="lock-icon" />
      <Box className="select-org-box">
        {isLoading ? (
          <Box className="mt-auto mb-auto">
            <Loader />
          </Box>
        ) : (
          <Formik
            initialValues={initialValues}
            onSubmit={handleOrganisationChange}
          >
            {({ handleSubmit, setFieldValue }) => (
              <Form onSubmit={handleSubmit} className="full-width">
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  className="org-selector-box"
                >
                  <Stack
                    direction="column"
                    justifyContent={
                      personalDetails.backend_user && !isLoading
                        ? "space-between"
                        : "center"
                    }
                    spacing={2}
                    className="organisation-box"
                  >
                    <>
                      <Typography
                        fontWeight={600}
                        fontSize={{ xs: "18px", md: "26px" }}
                        color={"#18112F"}
                      >
                        Change Organisations
                      </Typography>
                      <Stack
                        direction="column"
                        marginTop={{
                          xs: "28px !important",
                          sm: "56px !important",
                        }}
                        className="organisation-drop-down"
                        gap={"20px"}
                        justifyContent={"center"}
                      >
                        <span>
                          <Typography
                            className="organisation-page-heading"
                            marginBottom={"7px"}
                          >
                            Change your Organisation
                          </Typography>
                          <AutocompleteField
                            name="organisations"
                            icon={<></>}
                            mt18={false}
                            styleClass="custom-dropdown"
                            // disabled={selectedRole === "admin"}
                            dropdownIcon={
                              <ChevronRightIcon
                                sx={{
                                  color: "#C06DF3",
                                  width: "32px",
                                  height: "32px",
                                }}
                              />
                            }
                            trackingDetails={TRACKING_CHANGE_PORTAL_OPTIONS}
                            options={[...organisations].sort((a, b) =>
                              sortArray(a, b, "label")
                            )}
                            getOptions={(search) =>
                              getOrganisations(
                                search,
                                allOrganisations,
                                setOrganisations
                              )
                            }
                            getOptionById={(value) => {
                              setFieldValue("adminOrganisations", "");
                              getOrganisationById(
                                value,
                                allOrganisations,
                                false
                              );
                            }}
                          />
                        </span>
                        {(personalDetails?.is_employer_and_employee ||
                          personalDetails?.backend_user) && (
                          <span>
                            <Typography
                              className="mt-18 organisation-page-heading"
                              marginBottom={"7px"}
                            >
                              Change your admin Organisation
                            </Typography>
                            <AutocompleteField
                              name="adminOrganisations"
                              icon={<></>}
                              mt18={false}
                              styleClass="custom-dropdown"
                              dropdownIcon={
                                <ChevronRightIcon
                                  sx={{
                                    color: "#C06DF3",
                                    width: "32px",
                                    height: "32px",
                                  }}
                                />
                              }
                              PopperProps={{
                                modifiers: [
                                  {
                                    name: "preventOverflow",
                                    options: {
                                      altAxis: true, // allow reposition on Y-axis
                                      tether: false, // don't "stick" to input
                                    },
                                  },
                                ],
                              }}
                              // disabled={selectedRole === "employee"}
                              trackingDetails={TRACKING_CHANGE_PORTAL_OPTIONS}
                              options={[...adminOrganisations].sort((a, b) =>
                                sortArray(a, b, "label")
                              )}
                              getOptions={(search) =>
                                getOrganisations(
                                  search,
                                  allAdminOrganisations,
                                  setAdminOrganisations
                                )
                              }
                              getOptionById={(value) => {
                                setFieldValue("organisations", "");
                                getOrganisationById(
                                  value,
                                  allAdminOrganisations,
                                  true
                                );
                              }}
                            />
                          </span>
                        )}
                        <LoadingButton
                          buttonTitle="Proceed"
                          disabled={!selectedOrganisation}
                          trackingDetails={TRACKING_CHANGE_PORTAL_OPTIONS}
                          type="submit"
                          styleClass="btn select-org-btn mb-10 full-width"
                        />
                      </Stack>
                      {personalDetails.backend_user ? (
                        <>
                          <Box
                            className={tablet ? "mt-18" : ""}
                            sx={{ border: "1px solid #FAE2FE" }}
                          ></Box>
                          <Stack direction="column" alignItems={"center"}>
                            <Typography
                              className={`organisation-page-heading ${"mt-10"}`}
                            >
                              You have access to the backend admin area.
                            </Typography>
                            <LoadingButton
                              buttonTitle="Enter backend"
                              disabled={isLoading}
                              trackingDetails={TRACKING_CHANGE_PORTAL_OPTIONS}
                              handleClick={handelRedirectToBackendArea}
                              styleClass="btn select-org-btn mt-18 full-width"
                            />
                          </Stack>
                        </>
                      ) : null}
                    </>
                  </Stack>
                </Stack>
                <Box className="cancel-button-container">
                  <Button
                    className="font-semibold"
                    sx={{
                      textTransform: "none !important",
                      color: "#391474",
                      marginY: "0.5rem !important",
                      fontSize: {
                        xs: "16px !important",
                        md: "18px !important",
                      },
                      ":hover": {
                        textDecoration: "underline !important",
                      },
                    }}
                    onClick={goBack}
                  >
                    Cancel
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        )}
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    user_roles_organisation: state.organisation.user_roles_organisations,
  };
};

SelectOrgRolePage.propTypes = {
  user_roles_organisation: PropTypes.array,
};

export default connect(mapStateToProps)(SelectOrgRolePage);
